import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AccessTime,
  CalendarToday,
  FormatListNumbered,
  Group,
  ListAlt,
} from "@material-ui/icons";

import ListPagination from "../ListPagination";
import { formatDateYmd } from "../../utils/governingBodyApi";
import { PATHS } from "../../constants/paths";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(280px, 1fr))",
    gap: "16px",
    "@media (max-width: 1480px)": {
      gridTemplateColumns: "repeat(3, minmax(280px, 1fr))",
    },
    "@media (max-width: 1160px)": {
      gridTemplateColumns: "repeat(2, minmax(280px, 1fr))",
    },
    "@media (max-width: 760px)": {
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    borderRadius: "10px",
    border: "1px solid #d8e0ea",
    minHeight: "254px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.10)",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "20px",
  },
  header: {
    display: "flex",
    gap: "12px",
  },
  logoWrap: {
    width: "140px",
    minWidth: "140px",
    height: "110px",
    borderRadius: "8px",
    border: "1px solid #e8edf5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  logo: {
    maxWidth: "150px",
    maxHeight: "150px",
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  logoPlaceholder: {
    color: "#667085",
    fontSize: "12px",
  },
  id: {
    color: "#0d6efd",
    fontWeight: 700,
    fontSize: "12px",
  },
  abbreviation: {
    fontSize: "30px",
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1,
  },
  name: {
    fontSize: "16px",
    color: "#101828",
  },
  shortName: {
    fontSize: "14px",
    color: "#667085",
  },
  chipActive: {
    backgroundColor: "#dcfce7",
    color: "#166534",
    fontWeight: 700,
  },
  chipInactive: {
    backgroundColor: "#fef3c7",
    color: "#92400e",
    fontWeight: 700,
  },
  chipDeprecated: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    fontWeight: 700,
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "6px",
    columnGap: "10px",
  },
  detailLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#344054",
    fontSize: "13px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  updatedLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#667085",
    fontSize: "12px",
  },
  actions: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    padding: "0 20px 16px 20px",
  },
  actionBtn: {
    textTransform: "none",
    minWidth: "56px",
  },
  paginationWrap: {
    marginTop: "12px",
  },
  qualifyingScoresPill: {
    backgroundColor: "#e0e7ff",
    color: "#3730a3",
    fontWeight: 600,
    marginTop: "6px",
    cursor: "pointer",
  },
});

const getStatusClass = (classes, status) => {
  if (status === "ACTIVE") return classes.chipActive;
  if (status === "DEPRECATED") return classes.chipDeprecated;
  return classes.chipInactive;
};

const getAgeCutoff = (row) => {
  const month = row?.ageCutoffMonth ?? row?.ageCutOffMonth ?? row?.age_cutoff_month;
  const day = row?.ageCutoffDay ?? row?.ageCutOffDay ?? row?.age_cutoff_day;
  if (month == null || day == null || month === "" || day === "") return "";
  return `${month}/${day}`;
};

const GoverningBodyCardGrid = ({
  rows,
  pagination,
  onChangePage,
  onEdit,
  onDelete,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const goToQualifyingScores = (row) => {
    if (row?.id == null) return;
    navigate(`${PATHS.QUALIFYING_SCORES}?governingBodyId=${row.id}`);
  };

  return (
    <Box>
      <Box className={classes.grid}>
        {(rows || []).map((row) => {
          const logoUrl = row?.imageMasterUrl || row?.imageUrl || "";
          return (
          <Card key={row?.id} className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Box className={classes.header}>
                <Box className={classes.logoWrap}>
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={row.abbreviation || row.name || "logo"}
                      className={classes.logo}
                      onError={(event) => {
                        try {
                          event.currentTarget.style.display = "none";
                        } catch (_) {}
                      }}
                    />
                  ) : (
                    <Typography className={classes.logoPlaceholder}>No image</Typography>
                  )}
                </Box>

                <Box style={{ minWidth: 0 }}>
                  <Typography className={classes.id}>{row?.id}</Typography>
                  <Typography className={classes.abbreviation}>
                    {row?.abbreviation || "-"}
                  </Typography>
                  <Typography className={classes.name}>{row?.name || "-"}</Typography>
                  <Typography className={classes.shortName}>
                    {row?.shortName || "-"}
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      size="small"
                      label={row?.status || "UNKNOWN"}
                      className={getStatusClass(classes, row?.status)}
                    />
                  </Box>
                  <Chip
                    size="small"
                    clickable
                    label="Qualifying scores"
                    className={classes.qualifyingScoresPill}
                    onClick={() => goToQualifyingScores(row)}
                  />
                </Box>
              </Box>

              <Box className={classes.detailsGrid}>
                <Typography className={classes.detailLine}>
                  <FormatListNumbered style={{ fontSize: 16 }} />
                  Output: {row?.outputSequence ?? "-"}
                </Typography>
                <Typography className={classes.detailLine}>
                  <ListAlt style={{ fontSize: 16 }} />
                  DD List: {row?.ddListAbbreviation || row?.ddListDescription || "-"}
                </Typography>
                <Typography className={classes.detailLine}>
                  <Group style={{ fontSize: 16 }} />
                  Team: {row?.teamName || "-"}
                </Typography>
                <Typography className={classes.detailLine}>
                  <CalendarToday style={{ fontSize: 14 }} />
                  Cutoff: {getAgeCutoff(row) || "-"}
                </Typography>
                <Typography className={classes.detailLine}>
                  Parent: {row?.parentName || row?.parentShortName || row?.parentId || "-"}
                </Typography>
              </Box>

              <Typography className={classes.updatedLine}>
                <AccessTime style={{ fontSize: 14 }} />
                Updated: {formatDateYmd(row?.updatedAt) || "-"} by{" "}
                {row?.updatedByName || row?.updatedBy || "-"}
              </Typography>
            </CardContent>

            <CardActions className={classes.actions}>
              <Button
                className={classes.actionBtn}
                color="primary"
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
              <Button
                className={classes.actionBtn}
                color="secondary"
                onClick={() => onDelete(row)}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        )})}
      </Box>

      <Box className={classes.paginationWrap}>
        <ListPagination filters={pagination} fetchFunction={onChangePage} />
      </Box>
    </Box>
  );
};

GoverningBodyCardGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.shape({
    limit: PropTypes.number,
    page: PropTypes.number,
    total: PropTypes.number,
  }),
  onChangePage: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

GoverningBodyCardGrid.defaultProps = {
  rows: [],
  pagination: {
    limit: 20,
    page: 0,
    total: 0,
  },
};

export default GoverningBodyCardGrid;
